import { useDateFnsFormatString } from "Infrastructure/Hooks/DateFns/useDateFnsFormatString";
import { useDateFnsLocale } from "Infrastructure/Hooks/DateFns/useDateFnsLocale";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type SxProps, useTheme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export type DatePickerProps = {
  label?: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  id?: string;
  minDate?: Date;
  maxDate?: Date;
  disableFuture?: boolean;
  disablePast?: boolean;
  disabled?: boolean;
  error?: boolean;
  sx?: SxProps;
};

export const DatePickerInput = ({
  label,
  value,
  onChange,
  id,
  minDate,
  maxDate,
  disableFuture,
  disablePast,
  disabled,
  sx,
}: DatePickerProps) => {
  const { colors, radius, customSpacing } = useTheme();
  const { t } = useResource();

  const format = useDateFnsFormatString();
  const locale = useDateFnsLocale();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        format={format}
        label={label}
        value={value}
        onChange={(newValue) => onChange(newValue)}
        disableFuture={disableFuture}
        disablePast={disablePast}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            id,
            fullWidth: true,
            inputProps: {
              placeholder: t(Resources.Common.ChooseDate),
            },
            sx: {
              backgroundColor: colors.background.primary,
              borderRadius: radius.md,
              "& .MuiInputBase-root": {
                height: 40,
                borderRadius: radius.md,
                padding: `${customSpacing.xs} ${customSpacing.md}`,
                border: `1px solid ${colors.border.primary}`,
                backgroundColor: colors.background.primary,
                color: colors.text.primary,
                boxSizing: "border-box",
                transition: "border-color 0.2s, box-shadow 0.2s",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& input": {
                  padding: 0,
                },
                "& .MuiIconButton-root": {
                  padding: customSpacing.lg,
                },
              },
              ...sx,
            },
          },
          popper: {
            sx: {
              "& .MuiPaper-root": {
                borderRadius: radius.md,
                overflow: "hidden",
                mt: customSpacing.sm,
              },
              "& .MuiPickersDay-root.Mui-selected, \
               & .MuiPickersDay-root.Mui-selected:focus, \
               & .MuiPickersDay-root.Mui-selected.Mui-focusVisible, \
               & .MuiPickersDay-root.Mui-selected:active": {
                backgroundColor: colors.background.brandPrimary,
                color: colors.text.inverse,
                boxShadow: "none",
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};
